"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MessageSquare, ChartBar as BarChart3, Clock, ShieldCheck, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
const timelines = [{
  phase: "Months 1–2",
  title: "Technical fixes and foundation",
  description: "I resolve crawling barriers, indexation issues, and international site architecture gaps. You receive an initial prioritized backlog with clear impact scores."
}, {
  phase: "Months 3–4",
  title: "Search visibility and early traction",
  description: "Targeted commercial pages begin ranking for secondary terms across your target markets. Impressions rise in Google Search Console, and I track search intent alignment."
}, {
  phase: "Months 5–6+",
  title: "Commercial growth and qualified leads",
  description: "Priority keyword clusters can strengthen their search visibility as authority, relevance and implementation develop. Organic search traffic converts into verified sales inquiries, qualified pipeline value, or global ecommerce revenue."
}];
const principles = [{
  icon: MessageSquare,
  title: "Making the right hiring decision",
  description: "Evaluate your decision through team bandwidth, technical complexity, and strategic ownership. An independent consulting partnership provides senior-level advisory directly aligned with your commercial growth goals."
}, {
  icon: BarChart3,
  title: "Honest monthly reporting",
  description: "I provide actionable monthly summaries tracking qualified organic clicks, revenue metrics, and completed deliverables rather than bloated 80-page automated PDF exports."
}, {
  icon: Clock,
  title: "Pragmatic timelines",
  description: "Sustainable organic search takes focused execution. I provide honest estimates based on your domain history, competitive market landscape, and development bandwidth."
}, {
  icon: ShieldCheck,
  title: "Commercial accountability",
  description: "Every task I recommend connects directly to your bottom line, whether acquiring high-value enterprise B2B leads or accelerating international ecommerce orders."
}];
const expectationsDo = ["Communication across time zones via email, scheduled video calls, or shared Slack channels", "Tailored implementation backlogs with clear, developer-ready documentation", "Honest discussions on strategy traction, market algorithm shifts, and competitive moves", "A rigorous focus on search pipeline revenue and qualified conversions over vanity metrics"];
const expectationsDont = ["No unrealistic overnight top-ranking guarantees or risky automated backlink schemes", "No lock-in retainers, proprietary gated reporting, or opaque technical setups", "Consistent senior-level ownership and direct strategic execution on every deliverable", "No vague hourly billing — you always see exactly what was audited, built, and shipped"];
export default function Collaboration() {
  const shouldReduceMotion = useReducedMotion();
  const fadeVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="workingrelationshipandoutcomes" data-nav="light" className="bg-background py-20 md:py-28 border-b border-border text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeVariant} className="max-w-3xl mb-16">
          <Badge variant="secondary" className="mb-4">
            Working Relationship & Outcomes
          </Badge>
          <h2 className="text-2xl md:text-3xl font-semibold font-default tracking-tight text-foreground mb-4">
            What to Expect When You Hire Me.
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed">
            This is built on realistic timelines, honest milestones, and clear commercial expectations from day one.
          </p>
        </motion.div>

        {/* 4 Pillars of Working Relationship */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {principles.map((item, idx) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={idx} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeVariant}>
                <Card className="h-full border border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader className="flex flex-row items-start gap-4 pb-2">
                    <div className="p-2.5 rounded-md bg-secondary text-primary shrink-0">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold font-default text-card-foreground">
                        {item.title}
                      </h3>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Realistic Timelines Section */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeVariant} className="rounded-xl border border-border bg-card p-6 md:p-10 mb-16 shadow-sm">
          <div className="max-w-2xl mb-8">
            <h3 className="text-xl font-semibold font-default text-foreground mb-2">
              Realistic organic search timelines
            </h3>
            <p className="text-sm text-muted-foreground">
              Search engine optimization is a compounding strategic process, not an instant switch. While foundational technical fixes yield crawl improvements early, commercial growth across competitive search markets follows a structured progression.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {timelines.map((step, index) => <div key={step.phase} data-index={index} className="flex flex-col justify-between border-t md:border-t-2 md:border-l-0 border-primary/20 pt-4 md:pt-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {step.phase}
                  </span>
                  <h4 className="text-base font-medium font-default text-foreground mt-1 mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="size-4 text-primary" />
              <span>Timelines depend on domain history, market competition, and development velocity.</span>
            </div>
            <Link to="/SeoAuditsPage" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
              Get a tailored baseline audit
              <ArrowRight className="size-3" />
            </Link>
          </div>
        </motion.div>

        {/* Transparency: What to Expect vs What I Don't Do */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeVariant} className="rounded-lg border border-border p-6 bg-card/60">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="size-5 text-chart-4" />
              <h3 className="text-base font-semibold font-default text-foreground">
                What you can rely on
              </h3>
            </div>
            <ul className="space-y-3">
              {expectationsDo.map((text, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-chart-4 mt-2 shrink-0" />
                  <span>{text}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeVariant} className="rounded-lg border border-border p-6 bg-card/60">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="size-5 text-accent" />
              <h3 className="text-base font-semibold font-default text-foreground">
                What I never do
              </h3>
            </div>
            <ul className="space-y-3">
              {expectationsDont.map((text, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent mt-2 shrink-0" />
                  <span>{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Closing Action Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeVariant} className="rounded-xl bg-secondary/70 border border-border p-8 text-center flex flex-col items-center justify-center gap-4">
          <h3 className="text-xl font-semibold font-default text-foreground">
            Ready to discuss your organic growth?
          </h3>
          <p className="text-sm text-muted-foreground max-w-xl">
            Book a conversation directly with me. I will review your current search footprint, discuss practical timelines, and determine if an independent SEO consultancy model is right for your business.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/MonthlySeoServicesPage">Explore Monthly SEO Services</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}